import { useState, useCallback, useEffect, useRef } from "react";
import { SCRLR_CONFIG } from "../components/webplayerscrlr/config";

// Corner delta multipliers: [deltaX multiplier, deltaY multiplier]
const CORNER_DELTAS = {
  'bottom-right': [1, 1],
  'bottom-left': [-1, 1],
  'top-left': [-1, -1],
  'top-right': [1, -1]
};

/**
 * Custom hook for resize functionality with aspect ratio maintenance
 * @param {Object} options - Configuration options
 * @param {number} options.aspectRatio - Aspect ratio to maintain (default: from SCRLR_CONFIG)
 * @returns {Object} - { size, isResizing, handleResizeStart, elementRef }
 */
export default function useResize({ 
  aspectRatio = SCRLR_CONFIG.RESIZE.ASPECT_RATIO
} = {}) {
  const [size, setSize] = useState({ width: null, height: null });
  const [isResizing, setIsResizing] = useState(false);
  const resizeStartRef = useRef({ 
    mouseX: 0, 
    mouseY: 0, 
    startWidth: 0, 
    startHeight: 0,
    startLeft: 0,
    startBottom: 0,
    corner: null // 'bottom-left', 'bottom-right', 'top-left', or 'top-right'
  });
  const elementRef = useRef(null);

  // Helper functions for user selection
  const disableUserSelect = useCallback(() => {
    const body = document.body.style;
    body.userSelect = body.webkitUserSelect = body.mozUserSelect = body.msUserSelect = 'none';
  }, []);

  const enableUserSelect = useCallback(() => {
    const body = document.body.style;
    body.userSelect = body.webkitUserSelect = body.mozUserSelect = body.msUserSelect = '';
  }, []);

  const handleResizeStart = useCallback((e, corner) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    
    const computedStyle = window.getComputedStyle(elementRef.current);
    const rect = elementRef.current.getBoundingClientRect();
    const startLeft = parseFloat(computedStyle.left) || 0;
    const startBottom = parseFloat(computedStyle.bottom) || 0;
    
    disableUserSelect();
    
    resizeStartRef.current = {
      mouseX: e.clientX,
      mouseY: e.clientY,
      startWidth: rect.width,
      startHeight: rect.height,
      startLeft,
      startBottom,
      corner
    };
  }, [disableUserSelect]);

  // Helper function to parse CSS dimension values (px, vh, vw, %, none)
  const parseCSSDimension = (value) => {
    if (!value || value === 'none' || value === 'auto') return Infinity;
    
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return Infinity;
    
    if (value.includes('vh')) return (numValue / 100) * window.innerHeight;
    if (value.includes('vw')) return (numValue / 100) * window.innerWidth;
    if (value.includes('px')) return numValue;
    if (value.includes('%')) return numValue; // Limitation: percentage needs parent dimensions
    
    return numValue; // Assume pixels if no unit
  };
  
  // Helper to extract transform values
  const getTransform = (transform) => {
    if (!transform || transform === 'none') return { x: 0, y: 0 };
    const matrix = new DOMMatrix(transform);
    return { x: matrix.e, y: matrix.f };
  };

  const handleMouseMove = useCallback((e) => {
    if (!isResizing || !elementRef.current) return;
    
    const { 
      corner, 
      mouseX, 
      mouseY, 
      startWidth, 
      startHeight, 
      startLeft, 
      startBottom
    } = resizeStartRef.current;
    
    // Get CSS constraints from computed styles
    const computedStyle = window.getComputedStyle(elementRef.current);
    const minWidth = parseCSSDimension(computedStyle.minWidth);
    const maxWidth = parseCSSDimension(computedStyle.maxWidth);
    const minHeight = parseCSSDimension(computedStyle.minHeight);
    const maxHeight = parseCSSDimension(computedStyle.maxHeight);
    
    const deltaX = e.clientX - mouseX;
    const deltaY = e.clientY - mouseY;
    
    // Calculate width and height changes based on corner
    const [multX, multY] = CORNER_DELTAS[corner];
    const deltaWidth = deltaX * multX;
    const deltaHeight = deltaY * multY;
    
    // Calculate new dimensions maintaining aspect ratio (use dominant direction)
    const absDeltaX = Math.abs(deltaX);
    const absDeltaY = Math.abs(deltaY);
    
    let newWidth, newHeight;
    if (absDeltaX > absDeltaY) {
      newWidth = startWidth + deltaWidth;
      newHeight = newWidth / aspectRatio;
    } else {
      newHeight = startHeight + deltaHeight;
      newWidth = newHeight * aspectRatio;
    }
    
    // Apply CSS constraints while maintaining aspect ratio
    let finalWidth = Math.max(minWidth, Math.min(newWidth, maxWidth));
    let finalHeight = finalWidth / aspectRatio;
    
    // If height violates constraints, recalculate from height
    if (finalHeight < minHeight || finalHeight > maxHeight) {
      finalHeight = Math.max(minHeight, Math.min(finalHeight, maxHeight));
      finalWidth = finalHeight * aspectRatio;
      // Re-clamp width if needed
      finalWidth = Math.max(minWidth, Math.min(finalWidth, maxWidth));
      finalHeight = finalWidth / aspectRatio;
    }
    
    // Calculate new CSS positions to keep the opposite corner fixed
    const isLeftFixed = corner === 'bottom-right' || corner === 'top-right';
    const isBottomFixed = corner === 'top-left' || corner === 'top-right';
    
    const newLeft = isLeftFixed ? startLeft : startLeft + startWidth - finalWidth;
    const newBottom = isBottomFixed ? startBottom : startBottom + startHeight - finalHeight;

    // Check if element would be outside viewport (accounting for transform)
    const { x: transformX, y: transformY } = getTransform(computedStyle.transform);
    const visualLeft = newLeft + transformX;
    const visualTop = window.innerHeight - newBottom - finalHeight + transformY;
    
    const isInViewport = visualLeft >= 0 && 
                        visualTop >= 0 && 
                        visualLeft + finalWidth <= window.innerWidth && 
                        visualTop + finalHeight <= window.innerHeight;
    
    if (isInViewport && elementRef.current) {
      setSize({ width: finalWidth, height: finalHeight });
      const el = elementRef.current;
      el.style.width = `${finalWidth}px`;
      el.style.height = `${finalHeight}px`;
      el.style.left = `${newLeft}px`;
      el.style.bottom = `${newBottom}px`;
    }
  }, [isResizing, aspectRatio]);

  const handleMouseUp = useCallback(() => {
    setIsResizing(false);
    enableUserSelect();
  }, [enableUserSelect]);

  useEffect(() => {
    if (!isResizing) return;
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      enableUserSelect(); // Restore text selection if component unmounts or resize stops unexpectedly
    };
  }, [isResizing, handleMouseMove, handleMouseUp, enableUserSelect]);

  return { size, isResizing, handleResizeStart, elementRef };
}

